import { createSignal, onMount, For, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import ItemIdentifierPicker from '../../components/ItemIdentifierPicker';
import ItemStackView from '../../components/ItemStackView';
import { fromIdentifier } from '../../services/itemRefAdapters';
import { useCraftingResolver } from '../../services/craftingResolverContext';
import { useApi, isOk } from '../../api';
import type { FarmRunRecordingSummaryDto } from '../../api/generated/schemas';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import LocationPicker from '../../components/LocationPicker';
import { EmptyState } from '../../components/EmptyState';
import { useTranslation } from '../../hooks/useTranslation';
import { useUserContext } from '../../components/UserContextProvider';
import { useRoleParser } from '../../hooks/permissions';
import { formatDateTime } from '../../services/dateFormat';
import { downloadFromApi } from '../../services/downloadFile';
import BotScoped from '../../components/BotScoped';
import StorageSelect from '../../components/StorageSelect';
import styles from './Farm.module.css';
import { firstError } from '../../api/envelope';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

const FARM_MODE_FIXED = 0;
const FARM_MODE_WANDER = 1;

interface FarmConfig {
    id: string;
    name: string;
    citybuild: number;
    citybuildName: string;
    mode: number;
    wanderResumeStaleAfterHours: number;
    location: string;
    blockIdentifiers: string[];
    searchRadius: number;
    unloadStorageId: string;
    everythingExceptRepairable: boolean;
    itemIdentifierFilter: string[];
    minFreeSlots: number;
    compress: boolean;
    fetchLeftoverDrops: boolean;
    isEnabled: boolean;
    createdAt: string;
    minY: number | null;
    maxY: number | null;
}

const REC_END_KEYS: Record<string, string> = {
    Running: 'FARM_REC_END_RUNNING',
    Completed: 'FARM_REC_END_COMPLETED',
    Cancelled: 'FARM_REC_END_CANCELLED',
    StorageFull: 'FARM_REC_END_STORAGEFULL',
    NoTargets: 'FARM_REC_END_NOTARGETS',
    Unreachable: 'FARM_REC_END_UNREACHABLE',
    Error: 'FARM_REC_END_ERROR',
};

export default function FarmPage() {
    return <BotScoped groupAware><FarmInner /></BotScoped>;
}

function FarmInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const resolver = useCraftingResolver();

    const [configs, setConfigs] = createSignal<FarmConfig[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formCitybuild, setFormCitybuild] = createSignal(0);
    const [formMode, setFormMode] = createSignal(FARM_MODE_FIXED);
    const [formWanderResumeStaleAfterHours, setFormWanderResumeStaleAfterHours] = createSignal(12);
    const [formLocation, setFormLocation] = createSignal('');
    const [formBlockIdentifiers, setFormBlockIdentifiers] = createSignal<string[]>([]);
    const [formSearchRadius, setFormSearchRadius] = createSignal(64);
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formEverything, setFormEverything] = createSignal(true);
    const [formItemIdentifierFilter, setFormItemIdentifierFilter] = createSignal<string[]>([]);
    const [formMinFreeSlots, setFormMinFreeSlots] = createSignal(4);
    const [formCompress, setFormCompress] = createSignal(false);
    const [formFetchLeftovers, setFormFetchLeftovers] = createSignal(false);
    const [formIsEnabled, setFormIsEnabled] = createSignal(true);
    const [formMinY, setFormMinY] = createSignal<number | null>(null);
    const [formMaxY, setFormMaxY] = createSignal<number | null>(null);
    const [saving, setSaving] = createSignal(false);

    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';
    const inv = useInventoryApi();

    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const isAdmin = () => {
        const flags = parseRoles(user()?.permissions ?? 0, roles);
        return !!(flags[roles.indexOf('Admin')] || flags[roles.indexOf('Root')]) && user().showAdminUiInMemberApp === true;
    };

    const [recordings, setRecordings] = createSignal<FarmRunRecordingSummaryDto[]>([]);
    const [recLoading, setRecLoading] = createSignal(false);

    onMount(async () => {
        try {
            const [cfgRes, storageList] = await Promise.all([
                api.farm.getFarmConfigs(),
                inv.fetchStorages(),
            ]);
            if (isOk(cfgRes.data)) setConfigs((cfgRes.data.value.configs ?? []) as FarmConfig[]);
            setStorages(storageList);
        } catch { /* toast below */ }
        finally { setLoading(false); }
        if (isAdmin()) void loadRecordings();
    });

    async function loadRecordings() {
        if (!isAdmin()) return;
        setRecLoading(true);
        try {
            const res = await api.farm.getFarmRecordings();
            if (isOk(res.data)) setRecordings(res.data.value.recordings ?? []);
        } finally {
            setRecLoading(false);
        }
    }

    async function downloadRecording(r: FarmRunRecordingSummaryDto) {
        const failed = await downloadFromApi(`/api/farm/recordings/${r.id}/export`, `farm-recording-${r.id}.json`);
        if (failed) toast?.error(t('FARM_REC_DOWNLOAD_FAILED'));
    }

    function durationLabel(r: FarmRunRecordingSummaryDto): string {
        if (!r.endedAt) return t(REC_END_KEYS.Running);
        let s = Math.max(0, Math.round((new Date(r.endedAt).getTime() - new Date(r.startedAt).getTime()) / 1000));
        const h = Math.floor(s / 3600); s -= h * 3600;
        const m = Math.floor(s / 60); s -= m * 60;
        return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
    }

    function stallLabel(r: FarmRunRecordingSummaryDto): string {
        return `${Math.round(r.totalStallMs / 1000)}s`;
    }

    function issuesTotal(r: FarmRunRecordingSummaryDto): number {
        return r.pathfindFailures + r.reachFailures + r.breakTimeouts;
    }

    function outcomeClass(reason: string): string {
        if (reason === 'Completed' || reason === 'NoTargets') return styles.recOutcomeOk;
        if (reason === 'StorageFull' || reason === 'Unreachable' || reason === 'Error') return styles.recOutcomeBad;
        return styles.recOutcomeMuted;
    }

    async function loadConfigs() {
        const res = await api.farm.getFarmConfigs();
        if (isOk(res.data)) setConfigs((res.data.value.configs ?? []) as FarmConfig[]);
    }

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormCitybuild(0);
        setFormMode(FARM_MODE_FIXED);
        setFormWanderResumeStaleAfterHours(12);
        setFormLocation('');
        setFormBlockIdentifiers([]);
        setFormSearchRadius(64);
        setFormStorageId(storages()[0]?.id ?? '');
        setFormEverything(true);
        setFormItemIdentifierFilter([]);
        setFormMinFreeSlots(4);
        setFormCompress(false);
        setFormFetchLeftovers(false);
        setFormIsEnabled(true);
        setFormMinY(null);
        setFormMaxY(null);
        setShowForm(true);
    }

    function openEdit(c: FarmConfig) {
        setEditId(c.id);
        setFormName(c.name);
        setFormCitybuild(c.citybuild);
        setFormMode(c.mode);
        setFormWanderResumeStaleAfterHours(c.wanderResumeStaleAfterHours);
        setFormLocation(c.location);
        setFormBlockIdentifiers(c.blockIdentifiers ?? []);
        setFormSearchRadius(c.searchRadius);
        setFormStorageId(c.unloadStorageId);
        setFormEverything(c.everythingExceptRepairable);
        setFormItemIdentifierFilter(c.itemIdentifierFilter ?? []);
        setFormMinFreeSlots(c.minFreeSlots);
        setFormCompress(c.compress);
        setFormFetchLeftovers(c.fetchLeftoverDrops);
        setFormIsEnabled(c.isEnabled);
        setFormMinY(c.minY ?? null);
        setFormMaxY(c.maxY ?? null);
        setShowForm(true);
    }

    async function save(e: Event) {
        e.preventDefault();
        if (!formStorageId()) { toast?.error(t('FARM_SELECT_STORAGE')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || 'Farm Config',
            citybuild: formCitybuild(),
            mode: formMode(),
            wanderResumeStaleAfterHours: formWanderResumeStaleAfterHours(),
            location: formLocation().trim(),
            blockIdentifiers: formBlockIdentifiers(),
            searchRadius: formSearchRadius(),
            unloadStorageId: formStorageId(),
            everythingExceptRepairable: formEverything(),
            itemIdentifierFilter: formEverything() ? [] : formItemIdentifierFilter(),
            minFreeSlots: formMinFreeSlots(),
            compress: formCompress(),
            fetchLeftoverDrops: formFetchLeftovers(),
            isEnabled: formIsEnabled(),
            minY: formMinY(),
            maxY: formMaxY(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.farm.updateFarmConfig(id, body)
                : await api.farm.createFarmConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('FARM_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('FARM_UPDATED') : t('FARM_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('FARM_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.farm.deleteFarmConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('FARM_DELETE_FAILED'));
            return;
        }
        toast?.info(t('FARM_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('FARM_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.farm.runFarmConfig(id, { botId: targetBotId }),
            { success: t('FARM_STARTED'), failure: t('FARM_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('FARM_PICK_BOT')); return; }
        const failedIds = await fanOut.run(targets, targetBotId => api.farm.stopFarm(id, { botId: targetBotId }),
            { success: t('FARM_STOPPED'), failure: t('FARM_STOP_FAILED') });
        if (failedIds.length < targets.length && isAdmin()) window.setTimeout(loadRecordings, 1500);
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? id.slice(0, 8) + '...';
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="FARM_TITLE"/></h1>
                <button class="btn btn-primary" onClick={openCreate}>
                    <i class="fa-solid fa-plus" /> <Text val="FARM_NEW_CONFIG"/>
                </button>
            </div>
            <p class={styles.subtitle}><Text val="FARM_SUBTITLE"/></p>

            <Show when={loading()}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-spinner fa-spin" />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="FARM_EDIT"/> : <Text val="FARM_CREATE"/>}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_NAME"/></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('FARM_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_CITYBUILD"/></span>
                                <select class={styles.input} value={formCitybuild()} onChange={e => setFormCitybuild(parseInt(e.currentTarget.value))}>
                                    <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]}>{cb}</option>}</For>
                                </select>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_MODE"/></span>
                                <select class={styles.input} value={formMode()} onChange={e => setFormMode(parseInt(e.currentTarget.value))}>
                                    <option value={FARM_MODE_FIXED}>{t('FARM_MODE_FIXED')}</option>
                                    <option value={FARM_MODE_WANDER}>{t('FARM_MODE_WANDER')}</option>
                                </select>
                            </label>

                            <Show when={formMode() === FARM_MODE_FIXED}>
                                <label class={styles.field}>
                                    <span class={styles.fieldLabel}><Text val="FARM_F_LOCATION"/></span>
                                    <LocationPicker value={formLocation()} onChange={setFormLocation} optional />
                                </label>
                            </Show>

                            <Show when={formMode() === FARM_MODE_WANDER}>
                                <label class={styles.field}>
                                    <span class={styles.fieldLabel}><Text val="FARM_F_WANDER_STALE_HOURS"/></span>
                                    <input class={styles.input} type="number" min="1" max="168" value={formWanderResumeStaleAfterHours()} onInput={e => setFormWanderResumeStaleAfterHours(parseInt(e.currentTarget.value) || 12)} />
                                </label>
                            </Show>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_RADIUS"/></span>
                                <input class={styles.input} type="number" min="4" max="256" value={formSearchRadius()} onInput={e => setFormSearchRadius(parseInt(e.currentTarget.value) || 64)} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_STORAGE"/></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={formStorageId()}
                                    onChange={setFormStorageId}
                                    placeholder={t('FARM_SELECT_STORAGE_OPTION')}
                                />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_MINFREE"/></span>
                                <input class={styles.input} type="number" min="1" max="27" value={formMinFreeSlots()} onInput={e => setFormMinFreeSlots(parseInt(e.currentTarget.value) || 4)} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_MIN_Y"/></span>
                                <input class={styles.input} type="number" min="0" max="255" placeholder={String(0)} value={formMinY() ?? ''} onInput={e => { const v = parseInt(e.currentTarget.value); setFormMinY(isNaN(v) ? null : v); }} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_MAX_Y"/></span>
                                <input class={styles.input} type="number" min="0" max="255" placeholder={String(255)} value={formMaxY() ?? ''} onInput={e => { const v = parseInt(e.currentTarget.value); setFormMaxY(isNaN(v) ? null : v); }} />
                            </label>
                        </div>

                        <div class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="FARM_F_BLOCKS"/></span>
                            <ItemIdentifierPicker
                                value={formBlockIdentifiers()}
                                onChange={setFormBlockIdentifiers}
                                filter={(d) => d.id <= 255}
                                placeholder={t('FARM_BLOCKS_PLACEHOLDER')}
                                label="blocks"
                                metaAware
                                disabled={saving()}
                            />
                        </div>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formEverything()} onChange={e => setFormEverything(e.currentTarget.checked)} />
                            <span><Text val="FARM_F_EVERYTHING"/></span>
                        </label>

                        <Show when={!formEverything()}>
                            <div class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="FARM_F_FILTER"/></span>
                                <ItemIdentifierPicker
                                    value={formItemIdentifierFilter()}
                                    onChange={setFormItemIdentifierFilter}
                                    placeholder={t('FARM_FILTER_PLACEHOLDER')}
                                    label="items"
                                    disabled={saving()}
                                />
                            </div>
                        </Show>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formCompress()} onChange={e => setFormCompress(e.currentTarget.checked)} />
                            <span><Text val="FARM_F_COMPRESS"/></span>
                        </label>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formFetchLeftovers()} onChange={e => setFormFetchLeftovers(e.currentTarget.checked)} />
                            <span><Text val="FARM_F_FETCH_LEFTOVERS"/></span>
                        </label>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formIsEnabled()} onChange={e => setFormIsEnabled(e.currentTarget.checked)} />
                            <span><Text val="FARM_F_ENABLED"/></span>
                        </label>

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL"/>
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formStorageId()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE"/></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE"/>
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>


                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-wheat-awn" />
                        <p><Text val="FARM_EMPTY"/></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card} ${!c.isEnabled ? styles.cardDisabled : ''}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>
                                            {c.citybuildName}
                                            {c.mode === FARM_MODE_WANDER
                                                ? ` · ${t('FARM_MODE_WANDER')}`
                                                : c.location ? ` · ${c.location}` : ''}
                                        </div>
                                    </div>
                                    <span class={c.isEnabled ? styles.pillEnabled : styles.pillDisabled}>
                                        {c.isEnabled ? <Text val="FARM_ENABLED"/> : <Text val="FARM_DISABLED"/>}
                                    </span>
                                </div>

                                <div class={styles.blockChips}>
                                    <Show when={(c.blockIdentifiers?.length ?? 0) > 0} fallback={
                                        <span class={styles.noBlocks}><Text val="FARM_NO_BLOCKS"/></span>
                                    }>
                                        <For each={c.blockIdentifiers}>
                                            {(id) => (
                                                <div class={styles.blockChip} title={id}>
                                                    <ItemStackView stack={fromIdentifier(id, 0, resolver())} size={24} bare />
                                                </div>
                                            )}
                                        </For>
                                    </Show>
                                </div>

                                <div class={styles.cardBody}>
                                    <div class={styles.infoRow}>
                                        <i class="fa-solid fa-cube" />
                                        {c.blockIdentifiers?.length ?? 0} target{(c.blockIdentifiers?.length ?? 0) === 1 ? '' : 's'}
                                    </div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-magnifying-glass" /> radius {c.searchRadius}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-warehouse" /> {storageName(c.unloadStorageId)}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-box-open" /> unload below {c.minFreeSlots} free</div>
                                    <div class={styles.infoRow}>
                                        <i class="fa-solid fa-filter" />
                                        {c.everythingExceptRepairable
                                            ? 'all non-repairable'
                                            : `${c.itemIdentifierFilter?.length ?? 0} item${(c.itemIdentifierFilter?.length ?? 0) === 1 ? '' : 's'}`}
                                    </div>
                                </div>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="FARM_RUN"/>
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>

                <Show when={isAdmin()}>
                    <section class={styles.recSection}>
                        <div class={styles.recHeader}>
                            <div>
                                <h2 class={styles.recTitle}><Text val="FARM_REC_TITLE"/></h2>
                                <p class={styles.recSubtitle}><Text val="FARM_REC_SUBTITLE"/></p>
                            </div>
                            <button class="btn btn-secondary btn-icon" onClick={loadRecordings} title={t('REFRESH')} disabled={recLoading()}>
                                <i class={`fa-solid fa-rotate ${recLoading() ? 'fa-spin' : ''}`} />
                            </button>
                        </div>

                        <Show when={recLoading() && recordings().length === 0}>
                            <div class={styles.empty}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>
                        </Show>

                        <Show when={!recLoading() && recordings().length === 0}>
                            <EmptyState icon="fa-wave-square" title={t('FARM_REC_EMPTY')} />
                        </Show>

                        <Show when={recordings().length > 0}>
                            <div class={styles.recTableWrap}>
                                <table class={styles.recTable}>
                                    <thead>
                                        <tr>
                                            <th><Text val="FARM_REC_COL_RUN"/></th>
                                            <th><Text val="FARM_REC_COL_BOT"/></th>
                                            <th><Text val="FARM_REC_COL_STARTED"/></th>
                                            <th><Text val="FARM_REC_COL_DURATION"/></th>
                                            <th class={styles.num}><Text val="FARM_REC_COL_MINED"/></th>
                                            <th class={styles.num}><Text val="FARM_REC_COL_DEPOSITS"/></th>
                                            <th class={styles.num}><Text val="FARM_REC_COL_STALL"/></th>
                                            <th class={styles.num}><Text val="FARM_REC_COL_ISSUES"/></th>
                                            <th><Text val="FARM_REC_COL_OUTCOME"/></th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <For each={recordings()}>
                                            {(r) => (
                                                <tr>
                                                    <td>{r.configName}</td>
                                                    <td>{r.botUsername}</td>
                                                    <td>{formatDateTime(r.startedAt)}</td>
                                                    <td>{durationLabel(r)}</td>
                                                    <td class={styles.num}>{r.blocksMined}</td>
                                                    <td class={styles.num}>{r.depositCount}</td>
                                                    <td class={styles.num}>{stallLabel(r)}</td>
                                                    <td class={styles.num} title={t('FARM_REC_ISSUES_TOOLTIP', r.pathfindFailures, r.reachFailures, r.breakTimeouts, r.wanderEvents)}>{issuesTotal(r)}</td>
                                                    <td>
                                                        <span class={`${styles.recOutcome} ${outcomeClass(r.endReason)}`}>
                                                            {t(REC_END_KEYS[r.endReason] ?? REC_END_KEYS.Running)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button class="btn btn-secondary btn-sm" onClick={() => downloadRecording(r)} title={t('FARM_REC_DOWNLOAD')}>
                                                            <i class="fa-solid fa-download" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                        </For>
                                    </tbody>
                                </table>
                            </div>
                        </Show>
                    </section>
                </Show>
            </Show>
        </div>
    );
}
